import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import {
  useEditProviderUserInformation,
  useTransferProviderAccountOwnership,
} from '@/queries/provider-permission-management/mutations'
import {
  ProviderUser,
  ProviderUserRole,
  ProviderUserStatusCode,
} from '@/queries/provider-permission-management/schema'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog'
import { useState } from 'react'
import ManageUserForm, { UserFormValues } from './manage-user-form'

interface EditUserDialogProps {
  onOpenChange: (open: boolean) => void
  selectedContact: ProviderUser
  providerId: string
  roles: ProviderUserRole[]
  currentOwner: ProviderUser | null
}

function getDisplayName(user: ProviderUser) {
  return user.name ?? user.email
}

export default function EditUserDialog({
  onOpenChange,
  selectedContact,
  roles,
  providerId,
  currentOwner,
}: EditUserDialogProps) {
  // Holds the submitted values while the ownership transfer is being confirmed.
  const [pendingTransfer, setPendingTransfer] = useState<UserFormValues | null>(
    null
  )

  const editContactInformationMutation = useEditProviderUserInformation({
    providerId: providerId,
  })

  const transferOwnershipMutation = useTransferProviderAccountOwnership({
    providerId: providerId,
  })

  const isPending =
    editContactInformationMutation.status === 'pending' ||
    transferOwnershipMutation.status === 'pending'

  const ownerRoleId = roles
    .find((role) => role.role_code === 'OWNER')
    ?.role_id.toString()

  const isCurrentOwner = selectedContact.role.code === 'OWNER'

  const defaultValues = {
    firstName: selectedContact.first_name ?? '',
    lastName: selectedContact.last_name ?? '',
    email: selectedContact.email,
    role: selectedContact.role.id.toString(),
    status: selectedContact.status,
  }

  const getStatusToSend = (status: UserFormValues['status']) =>
    status === ProviderUserStatusCode.ACTIVE ||
    status === ProviderUserStatusCode.INACTIVE
      ? status
      : undefined

  const close = () => {
    setPendingTransfer(null)
    onOpenChange(false)
  }

  const handleSubmit = (data: UserFormValues) => {
    // Only one account owner can exist, so promoting this user takes the role
    // away from whoever holds it. Confirm before doing that, and route it
    // through the dedicated ownership endpoint rather than a plain role edit.
    const isTakingOwnership =
      ownerRoleId !== undefined && data.role === ownerRoleId && !isCurrentOwner

    if (isTakingOwnership) {
      setPendingTransfer(data)
      return
    }

    editContactInformationMutation.mutate(
      {
        email: data.email,
        userId: selectedContact.id,
        status: getStatusToSend(data.status),
        firstName: data.firstName,
        lastName: data.lastName,
        roleId: data.role,
      },
      { onSuccess: close }
    )
  }

  const handleConfirmTransfer = () => {
    if (!pendingTransfer) {
      return
    }

    // The ownership endpoint only moves the role, so any other field the admin
    // edited in the same submit is saved alongside it.
    const hasProfileChanges =
      pendingTransfer.firstName !== defaultValues.firstName ||
      pendingTransfer.lastName !== defaultValues.lastName ||
      pendingTransfer.email !== defaultValues.email ||
      pendingTransfer.status !== defaultValues.status

    transferOwnershipMutation.mutate(
      {
        providerUserId: selectedContact.id,
        profile: hasProfileChanges
          ? {
              firstName: pendingTransfer.firstName,
              lastName: pendingTransfer.lastName,
              email: pendingTransfer.email,
              // Keep the current role here — the transfer is what promotes them.
              roleId: defaultValues.role,
              status: getStatusToSend(pendingTransfer.status),
            }
          : undefined,
      },
      { onSuccess: close }
    )
  }

  const handleOpenChange = (newOpen: boolean) => {
    // Prevent closing the dialog when a mutation is pending
    if (!newOpen && isPending) {
      return
    }
    onOpenChange(newOpen)
  }

  const previousOwner =
    currentOwner && currentOwner.id !== selectedContact.id ? currentOwner : null

  return (
    <>
      <Dialog open={true} onOpenChange={handleOpenChange}>
        <DialogContent
          className="max-w-lg"
          onInteractOutside={(e) => {
            if (isPending) {
              e.preventDefault()
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>

          <ManageUserForm
            mutationStatus={editContactInformationMutation.status}
            roles={roles}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
            submitButtonText="Save Changes"
            showStatusToggle={true}
            roleSelectionDisabled={isCurrentOwner}
            roleSelectionHelperText={
              isCurrentOwner
                ? 'This user is the account owner. Ownership can only be moved by assigning the Account Owner role to another user.'
                : undefined
            }
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingTransfer !== null}
        onOpenChange={(open) => {
          if (!open && !isPending) {
            setPendingTransfer(null)
          }
        }}
      >
        <DialogContent
          onInteractOutside={(e) => {
            if (isPending) {
              e.preventDefault()
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="font-medium text-gray-800">
              Transfer Account Ownership
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            {getDisplayName(selectedContact)} will become the account owner for
            this provider.
            {previousOwner
              ? ` A provider can only have one account owner, so ${getDisplayName(
                  previousOwner
                )} will no longer be the account owner.`
              : ' A provider can only have one account owner.'}
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingTransfer(null)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <div>
              <SubmitButton
                type="button"
                buttonTxt="Transfer Ownership"
                submittingText="Transferring Ownership..."
                status={transferOwnershipMutation.status}
                onClick={handleConfirmTransfer}
              />
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
